import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";

const EMPTY = { email: "", name: "", role: "employee", password: "", active: true };
const ROLES = { admin: "Admin (everything)", employee: "Employee (bookings, partners, payments, complaints)", support: "Customer support (complaints, chats, view bookings, KYC)", kyc: "KYC team (verify documents only)" };

export const AdminTeam = ({ me }) => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/team").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (editing) await api.put(`/admin/team/${encodeURIComponent(editing)}`, f);
      else await api.post("/admin/team", f);
      toast.success(editing ? "Team account updated" : "Team account created");
      if (editing === me && f.email.trim().toLowerCase() !== me) toast.info("You changed your own login email. Please log in again.");
      setF(EMPTY); setEditing(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-5xl space-y-5">
      <p className="text-sm text-[#4A6358]">Create logins for your office team. They sign in at the same hidden admin page with the email and password you set here. You can change anyone's login email, password or role at any time.</p>
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-[#D2E0D8] bg-white p-4" data-testid="team-form">
        <label className="text-xs font-semibold">Login email<Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-10 w-56" data-testid="team-email-input" /></label>
        <label className="text-xs font-semibold">Name<Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-10 w-44" data-testid="team-name-input" /></label>
        <label className="text-xs font-semibold">Role
          <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="block h-10 w-56 rounded-md border border-input px-2 text-sm" data-testid="team-role-select">
            {Object.entries(ROLES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="text-xs font-semibold">{editing ? "New password (blank = keep)" : "Password"}<Input type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="h-10 w-44" data-testid="team-password-input" /></label>
        <button onClick={save} className="h-10 rounded-xl bg-[#0D3B2B] px-5 font-bold text-white" data-testid="team-save-btn">{editing ? "Save changes" : "Add team member"}</button>
        {editing && <button onClick={() => { setEditing(null); setF(EMPTY); }} className="h-10 px-3 font-semibold text-[#4A6358]" data-testid="team-cancel-btn">Cancel</button>}
      </div>
      <div className="overflow-x-auto rounded-2xl border border-[#D2E0D8] bg-white">
        <table className="w-full text-left text-sm" data-testid="team-table">
          <thead className="bg-[#EAEFEA] text-xs uppercase text-[#4A6358]"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Login email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.email} className="border-t border-[#EAEFEA]" data-testid={`team-row-${r.email}`}>
                <td className="px-4 py-3 font-semibold">{r.name}{r.email === me && " (you)"}</td><td className="px-4 py-3">{r.email}</td>
                <td className="px-4 py-3 capitalize">{r.role}</td><td className="px-4 py-3">{r.active ? "Active" : "Off"}</td>
                <td className="space-x-3 px-4 py-3">
                  <button onClick={() => { setEditing(r.email); setF({ email: r.email, name: r.name || "", role: r.role, password: "", active: r.active }); }} className="font-semibold text-[#2E9E6B]" data-testid={`team-edit-${r.email}`}>Edit</button>
                  {r.email !== me && <button onClick={() => api.put(`/admin/team/${encodeURIComponent(r.email)}`, { ...r, password: "", active: !r.active }).then(load).catch((e) => toast.error(errMsg(e)))}
                    className="font-semibold text-[#E05C3A]" data-testid={`team-toggle-${r.email}`}>{r.active ? "Switch off" : "Switch on"}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
